// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import {
  Component,
  ChangeDetectionStrategy,
  computed,
  input,
} from '@angular/core';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import { TreemapDataItem, TreemapColorStyle } from './treemap-echarts.models';

@Component({
  selector: 'lfx-treemap-echarts',
  templateUrl: './treemap-echarts.component.html',
  styleUrls: ['./treemap-echarts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgxEchartsModule],
  host: {
    '[style.width]': '"100%"',
    '[style.height]': '"100%"',
    '[style.display]': '"block"',
  },
})
export class TreemapEchartsComponent {
  readonly data = input.required<TreemapDataItem[]>();
  readonly seriesName = input<string>('Treemap');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly tooltipFormatter = input<((params: any) => string) | undefined>();
  readonly roam = input<boolean>(false);
  readonly nodeClick = input<boolean>(false);
  readonly hideValue = input<boolean>(false);
  readonly disableTooltip = input<boolean>(false);
  readonly customOptions = input<Partial<EChartsOption>>();
  readonly loading = input<boolean>(false);

  private readonly COLOR_STYLES: TreemapColorStyle[] = [
    {
      color: '#83C1FF',
      hover: '#6BB0FF',
      borderColor: '#FFFFFF',
      borderWidth: 2,
    },
    {
      color: '#ADD6FF',
      hover: '#95C7FF',
      borderColor: '#FFFFFF',
      borderWidth: 2,
    },
    {
      color: '#D6EBFF',
      hover: '#BFDDFF',
      borderColor: '#FFFFFF',
      borderWidth: 2,
    },
    {
      color: '#EBF5FF',
      hover: '#D6EBFF',
      borderColor: '#FFFFFF',
      borderWidth: 2,
    },
  ];

  private readonly LABEL_STYLE_DEFAULT = {
    icon1: {
      backgroundColor: {
        image: this.getSignalBarDataUri('green'),
      },
      height: 16,
      width: 18,
    },
    icon2: {
      backgroundColor: {
        image: this.getSignalBarDataUri('blue'),
      },
      height: 16,
      width: 18,
    },
    icon3: {
      backgroundColor: {
        image: this.getSignalBarDataUri('yellow'),
      },
      height: 16,
      width: 18,
    },
    icon4: {
      backgroundColor: {
        image: this.getSignalBarDataUri('red'),
      },
      height: 16,
      width: 18,
    },
    a: {
      fontSize: 14,
      fontWeight: 600,
      fontFamily: 'Inter, sans-serif',
      color: '#111827',
      lineHeight: 24,
    },
    b: {
      fontSize: 24,
      fontWeight: 600,
      fontFamily: 'Inter, sans-serif',
      color: '#111827',
      lineHeight: 40,
    },
    c: {
      fontSize: 12,
      fontWeight: 400,
      fontFamily: 'Inter, sans-serif',
      color: '#111827',
      opacity: 0.6,
    },
  };

  protected chartOption = computed(() => {
    return this.getTreemapConfig();
  });

  private getTreemapConfig(): EChartsOption {
    const data = this.data();
    if (!data || data.length === 0) {
      return {};
    }

    const styledData = this.mapDataStyles(data);
    const seriesName = this.seriesName();
    const customOptions = this.customOptions();

    const defaultConfig: EChartsOption = {
      tooltip: this.disableTooltip()
        ? {
            show: false,
          }
        : {
            borderColor: '#E5E7EB',
            borderWidth: 1,
            trigger: 'item',
            confine: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter:
              this.tooltipFormatter() ||
              ((params: any) => {
                const dataItem = params.data as TreemapDataItem;
                const valueArray = params.value as [number, number];
                const value = this.formatNumber(valueArray[0]);
                const subtext = dataItem.subtext || '';
                return `
            <div style="font-weight: 600; margin-bottom: 4px; font-size: 14px;">${params.name}</div>
            ${subtext ? `<div style="font-size: 12px; color: #6B7280; margin-bottom: 4px;">${subtext}</div>` : ''}
            <div style="font-size: 18px; font-weight: 600; margin-bottom: 2px;">${value}</div>
            <div style="font-size: 12px; opacity: 0.7;">${seriesName}</div>
          `;
              }),
          },
      series: [
        {
          name: seriesName,
          type: 'treemap',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          breadcrumb: {
            show: false,
          },
          roam: this.roam(),
          nodeClick: this.nodeClick() ? 'link' : false,
          visibleMin: 0,
          visualMin: 1,
          visualMax: 4,
          visualDimension: 1,
          ...(this.disableTooltip() && {
            cursor: 'default',
          }),
          label: {
            show: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter: (params: any) => {
              const dataItem = params.data as TreemapDataItem;
              const valueArray = params.value as [number, number];
              const value = this.formatNumber(valueArray[0]);
              const subtext = dataItem.subtext || seriesName;
              const signalType = dataItem.signalType;
              const hideValue = this.hideValue();

              if (hideValue) {
                if (signalType) {
                  const iconTokenKey = this.getIconToken(signalType);
                  return `{a|${params.name}}\n{${iconTokenKey}|} {c|${subtext}}`;
                } else {
                  return `{a|${params.name}}\n{c|${subtext}}`;
                }
              } else {
                if (signalType) {
                  const iconTokenKey = this.getIconToken(signalType);
                  return `{a|${params.name}}\n{b|${value}}\n{${iconTokenKey}|} {c|${subtext}}`;
                } else {
                  return `{a|${params.name}}\n{b|${value}}\n{c|${subtext}}`;
                }
              }
            },
            offset: [12, 12],
            padding: [0, 12, 10, 0],
            rich: this.LABEL_STYLE_DEFAULT,
            color: '#111827',
          },
          ...(this.disableTooltip() && {
            emphasis: {
              disabled: true,
            },
          }),
          levels: [
            {
              colorMappingBy: 'value',
              itemStyle: {
                gapWidth: 4,
              },
            },
            {
              itemStyle: {
                borderRadius: 4,
                gapWidth: 4,
              },
            },
          ],
          data: styledData,
        },
      ],
    };

    return customOptions
      ? this.deepMerge(defaultConfig, customOptions)
      : defaultConfig;
  }

  private mapDataStyles(data: TreemapDataItem[]): TreemapDataItem[] {
    const tooltipDisabled = this.disableTooltip();
    
    return data.map(item => {
      const colorLevel = Math.max(1, Math.min(4, item.value[1])) - 1;
      const style = this.COLOR_STYLES[colorLevel] || this.COLOR_STYLES[0];

      const styledItem: TreemapDataItem = {
        ...item,
        itemStyle: {
          ...item.itemStyle,
          color: style.color,
          borderColor: style.borderColor,
          borderWidth: style.borderWidth,
        },
      };

      // Only add hover effects if tooltip is enabled
      if (!tooltipDisabled) {
        styledItem.emphasis = {
          itemStyle: {
            color: style.hover,
            borderColor: style.borderColor,
            borderWidth: style.borderWidth,
          },
        };
      }

      return styledItem;
    });
  }

  private getSignalBarDataUri(
    signalType: 'green' | 'blue' | 'yellow' | 'red'
  ): string {
    const svgMap: Record<string, string> = {
      green:
        '<svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.5 0C17.3125 0 18 0.6875 18 1.5V14.5C18 15.3438 17.3125 16 16.5 16C15.6562 16 15 15.3438 15 14.5V1.5C15 0.6875 15.6562 0 16.5 0ZM10 5.5C10 4.6875 10.6562 4 11.5 4C12.3125 4 13 4.6875 13 5.5V14.5C13 15.3438 12.3125 16 11.5 16C10.6562 16 10 15.3438 10 14.5V5.5ZM6.5 8C7.3125 8 8 8.6875 8 9.5V14.5C8 15.3438 7.3125 16 6.5 16C5.65625 16 5 15.3438 5 14.5V9.5C5 8.6875 5.65625 8 6.5 8ZM1.5 12C2.3125 12 3 12.6875 3 13.5V14.5C3 15.3438 2.3125 16 1.5 16C0.65625 16 0 15.3438 0 14.5V13.5C0 12.6875 0.65625 12 1.5 12Z" fill="#10BC8A"/></svg>',
      blue: '<svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 4C12.3125 4 13 4.6875 13 5.5V14.5C13 15.3438 12.3125 16 11.5 16C10.6562 16 10 15.3438 10 14.5V5.5C10 4.6875 10.6562 4 11.5 4ZM5 9.5C5 8.6875 5.65625 8 6.5 8C7.3125 8 8 8.6875 8 9.5V14.5C8 15.3438 7.3125 16 6.5 16C5.65625 16 5 15.3438 5 14.5V9.5ZM0 13.5C0 12.6875 0.65625 12 1.5 12C2.3125 12 3 12.6875 3 13.5V14.5C3 15.3438 2.3125 16 1.5 16C0.65625 16 0 15.3438 0 14.5V13.5Z" fill="#009AFF"/><path opacity="0.4" d="M15 1.5C15 0.6875 15.6562 0 16.5 0C17.3125 0 18 0.6875 18 1.5V14.5C18 15.3438 17.3125 16 16.5 16C15.6562 16 15 15.3438 15 14.5V1.5Z" fill="#009AFF"/></svg>',
      yellow:
        '<svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.5 8C7.3125 8 8 8.6875 8 9.5V14.5C8 15.3438 7.3125 16 6.5 16C5.65625 16 5 15.3438 5 14.5V9.5C5 8.6875 5.65625 8 6.5 8ZM1.5 12C2.3125 12 3 12.6875 3 13.5V14.5C3 15.3438 2.3125 16 1.5 16C0.65625 16 0 15.3438 0 14.5V13.5C0 12.6875 0.65625 12 1.5 12Z" fill="#F89E22"/><path opacity="0.4" d="M16.5 0C17.3125 0 18 0.6875 18 1.5V14.5C18 15.3438 17.3125 16 16.5 16C15.6562 16 15 15.3438 15 14.5V1.5C15 0.6875 15.6562 0 16.5 0ZM11.5 4C12.3125 4 13 4.6875 13 5.5V14.5C13 15.3438 12.3125 16 11.5 16C10.6562 16 10 15.3438 10 14.5V5.5C10 4.6875 10.6562 4 11.5 4Z" fill="#F89E22"/></svg>',
      red: '<svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 13.5C0 12.6875 0.65625 12 1.5 12C2.3125 12 3 12.6875 3 13.5V14.5C3 15.3438 2.3125 16 1.5 16C0.65625 16 0 15.3438 0 14.5V13.5Z" fill="#EF4444"/><path opacity="0.4" d="M16.5 0C17.3125 0 18 0.6875 18 1.5V14.5C18 15.3438 17.3125 16 16.5 16C15.6562 16 15 15.3438 15 14.5V1.5C15 0.6875 15.6562 0 16.5 0ZM10 5.5C10 4.6875 10.6562 4 11.5 4C12.3125 4 13 4.6875 13 5.5V14.5C13 15.3438 12.3125 16 11.5 16C10.6562 16 10 15.3438 10 14.5V5.5ZM5 9.5C5 8.6875 5.65625 8 6.5 8C7.3125 8 8 8.6875 8 9.5V14.5C8 15.3438 7.3125 16 6.5 16C5.65625 16 5 15.3438 5 14.5V9.5Z" fill="#EF4444"/></svg>',
    };
    return `data:image/svg+xml;base64,${btoa(svgMap[signalType])}`;
  }

  private getIconToken(
    signalType: 'green' | 'blue' | 'yellow' | 'red'
  ): string {
    const iconMap: Record<string, string> = {
      green: 'icon1',
      blue: 'icon2',
      yellow: 'icon3',
      red: 'icon4',
    };
    return iconMap[signalType];
  }

  private formatNumber(num: number): string {
    if (num >= 1000000000000) {
      return (num / 1000000000000).toFixed(1) + 'T';
    } else if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1) + 'B';
    } else if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  }

  public static normalizeColorLevel(
    value: number,
    min: number,
    max: number
  ): number {
    if (min === max) return 2;
    const normalized = ((value - min) / (max - min)) * 3 + 1;
    return Math.min(4, Math.max(1, Math.round(normalized)));
  }

  private deepMerge(
    target: Record<string, unknown>,
    source: Record<string, unknown>
  ): Record<string, unknown> {
    const output = { ...target };
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = this.deepMerge(
              target[key] as Record<string, unknown>,
              source[key] as Record<string, unknown>
            );
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    return output;
  }

  private isObject(item: unknown): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }
}
